"use client";

import {useState} from "react";
import SectionTitle, {SectionTitleTheme} from "@/app/components/atomic/molecules/SectionTitle";
import AppIcon from "@/app/components/atomic/AppIcon";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";

const VISIBLE_COUNT = 5;

const list: Record<string, { color: SectionTitleTheme; links: { name: string; link: string; logo: string }[] }> = {
    "Assurance emprunteur": {
        color: "pastel-yellow",
        links: [
            {name: "Assurance emprunteur", link: "/simulation-assurance-pret-immobilier", logo: "fa-house-laptop"},
            {name: "Assurance PNO", link: "/guide/assurance-habitation/comparateur-assurance-pno", logo: "fa-key-skeleton"},
            {name: "Loyer impayé", link: "/guide/assurance-loyer-impaye", logo: "fa-key"},
            {name: "Dommages ouvrage", link: "/guide/assurance-dommages-ouvrage", logo: "fa-house-circle-check"},
            {name: "Garantie décennale", link: "/guide/pro/garantie-decennale", logo: "fa-trowel-bricks"},
            {name: "RC professionelle", link: "/guide/pro/responsabilite-civile", logo: "fa-house-laptop"},
            {name: "Vente immobilière", link: "/guide/immobilier", logo: "fa-buildings"},
        ],
    },
    "Prêt immo et finances": {
        color: "pastel-pink",
        links: [
            {name: "Prêt immobilier", link: "/guide/pret-immobilier", logo: "fa-house"},
            {name: "Rachat crédit", link: "/guide/rachat-credit", logo: "fa-hand-holding-circle-dollar"},
            {name: "Crédit consommation", link: "/guide/credit-consommation", logo: "fa-dollar"},
            {name: "Plan épargne retraite", link: "/guide/per/meilleur", logo: "fa-money-bill-trend-up"},
            {name: "Bourse / Placement", link: "/guide/assurance-vie/placement", logo: "fa-money-bill-trend-up"},
            {name: "Banque", link: "/guide/banque", logo: "fa-building-columns"},
            {name: "Impôts", link: "/guide/impots", logo: "fa-file-invoice-dollar"},
            // {name: "Cashback", link: "/guide/cashback", logo: "fa-hand-holding-dollar"},
            {name: "SCPI", link: "/guide/epargne/scpi", logo: "fa-buildings"},
            {name: "Guide assurances pro", link: "/guide/pro", logo: "fa-buildings"},
        ],
    },
    "Mutuelle et Prévoyance": {
        color: "pastel-orange",
        links: [
            {name: "Mutuelle santé", link: "/comparateur-mutuelle-sante", logo: "fa-heart"},
            {name: "Mutuelle expatrié", link: "/guide/mutuelle-sante/expatries", logo: "fa-plane"},
            {name: "Décès", link: "/guide/assurance-deces", logo: "fa-cross"},
            {name: "Dépendance", link: "/guide/assurance-dependance", logo: "fa-family"},
            {name: "Obsèques", link: "/guide/assurance-obseques", logo: "fa-tombstone"},
            {name: "GAV", link: "/guide/assurance-garantie-accidents-vie", logo: "fa-bone-break"},
            {name: "Assurance vie", link: "/guide/assurance-vie", logo: "fa-shield-check"},
            {name: "Accidents chômage", link: "/guide/assurance-chomage", logo: "fa-house"},
        ],
    },
    "Nos assurances": {
        color: "pastel-blue",
        links: [
            {name: "Assurance voyage", link: "/guide/assurance-voyage", logo: "fa-plane"},
            {name: "Assurance animaux", link: "/guide/assurance-chien-chat", logo: "fa-paw"},
            {name: "Assurance vélo", link: "/guide/assurance-loisirs/velo", logo: "fa-bicycle"},
            {name: "Assurance auto", link: "/guide/assurance-auto", logo: "fa-car"},
            {name: "Assurance moto", link: "/guide/assurance-moto", logo: "fa-motorcycle"},
            {name: "Assurance bateau", link: "/guide/assurance-bateau", logo: "fa-sailboat"},
            {name: "Assurance habitation", link: "/guide/assurance-habitation", logo: "fa-house-chimney"},
            {name: "Assurance mobile", link: "/guide/assurance-mobile", logo: "fa-mobile"},
            {name: "Assurance juridique", link: "/guide/protection-juridique", logo: "fa-gavel"},
            {name: "Assurance scolaire", link: "/guide/assurance-scolaire", logo: "fa-graduation-cap"},
            {name: "Assurance loisirs", link: "/guide/assurance-loisirs", logo: "fa-futbol"},
            {name: "Responsabilité civile", link: "/guide/responsabilite-civile", logo: "fa-family"},
        ],
    },
};

const colorMap: Record<string, { text: string; shadow: string }> = {
    "pastel-yellow": {text: "text-pastel-yellow", shadow: "shadow-pastel-yellow"},
    "pastel-orange": {text: "text-pastel-orange", shadow: "shadow-pastel-orange"},
    "pastel-pink": {text: "text-pastel-pink", shadow: "shadow-pastel-pink"},
    "pastel-blue": {text: "text-pastel-blue", shadow: "shadow-pastel-blue"},
};

function toSlug(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-");
}

function LinkItem({item, color}: { item: { name: string; link: string; logo: string }; color: string }) {
    const {text, shadow} = colorMap[color] ?? {text: "", shadow: ""};
    return (
        <a
            href={item.link}
            className={`flex items-center gap-3 px-5 py-3 bg-white transition hover:bg-gray-50 rounded-xl ${shadow}`}
        >
            <AppIcon iconClassName={item.logo} size="lg" className={text} />
            <span>{item.name}</span>
        </a>
    );
}

export default function ComparatorList() {
    const [openSection, setOpenSection] = useState<string | null>(null);
    const [showAll, setShowAll] = useState(false);

    const toggle = (key: string) => {
        setOpenSection(prev => prev === key ? null : key);
    };

    const hasExtra = Object.values(list).some(({links}) => links.length > VISIBLE_COUNT);

    return (
        <div id="comparators" className="flex flex-col items-center gap-6 w-full max-w-xl lg:max-w-none mx-auto lg:px-6 pb-10 lg:pb-20 scroll-mt-24">
            <span className="max-w-[280px] lg:max-w-[390px] text-center">
                <SectionTitle
                    level={"h3"}
                    theme={"white"}
                >
                    Un comparateur d'assurance pour tous vos besoins.
                </SectionTitle>
            </span>

            <div className="flex flex-col lg:flex-wrap lg:flex-row gap-3 lg:gap-6 w-full max-w-[1200px] items-center lg:items-start">
                {Object.entries(list).map(([title, {color, links}]) => {
                    const isOpen = openSection === title;
                    const slug = toSlug(title);
                    const visibleLinks = links.slice(0, VISIBLE_COUNT);
                    const extraLinks = links.slice(VISIBLE_COUNT);

                    return (
                        <div key={title} className="overflow-hidden p-1 -mx-1 -mb-1 lg:overflow-visible lg:w-[calc(50%-0.75rem)] lg:flex-1 w-full max-w-[400px] lg:max-w-none">
                            <button
                                onClick={() => toggle(title)}
                                aria-expanded={isOpen}
                                aria-controls={`section-${slug}`}
                                className="flex items-center justify-between w-full py-1 md:py-4 cursor-pointer lg:pointer-events-none lg:cursor-default"
                            >
                                <SectionTitle level={"h3"} theme={color}>
                                    {title}
                                </SectionTitle>

                                <span className="lg:hidden">
                                    <ChevronIcon open={isOpen} />
                                </span>
                            </button>

                            <div
                                id={`section-${slug}`}
                                role="region"
                                aria-label={title}
                                className="transition-all duration-300 ease-in-out p-1 -mx-1 overflow-hidden lg:overflow-visible lg:!max-h-none lg:!opacity-100"
                                style={{
                                    maxHeight: isOpen ? `${links.length * 56}px` : "0px",
                                    opacity: isOpen ? 1 : 0,
                                }}
                            >
                                <div className="flex flex-col gap-2">
                                    {visibleLinks.map((item, i) => (
                                        <LinkItem key={i} item={item} color={color} />
                                    ))}
                                </div>

                                {/* Extra links: visible in mobile accordion, desktop toggles via showAll */}
                                {extraLinks.length > 0 && (
                                    <div
                                        className={`flex flex-col gap-2 mt-2 lg:transition-all lg:duration-300 lg:ease-in-out lg:overflow-hidden lg:p-1 lg:-mx-1 lg:-mb-1 ${
                                            showAll ? "" : "lg:!max-h-0 lg:!opacity-0 lg:!mt-0"
                                        }`}
                                        style={showAll ? {maxHeight: `${extraLinks.length * 56}px`} : undefined}
                                    >
                                        {extraLinks.map((item, i) => (
                                            <LinkItem key={`extra-${i}`} item={item} color={color} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {hasExtra && (
                <button
                    onClick={() => setShowAll(prev => !prev)}
                    className="hidden lg:flex items-center gap-2 px-6 py-3 cursor-pointer text-pale text-sm"
                >
                    <span className="underline">{showAll ? "Voir moins" : "Voir tous nos comparateurs"}</span>
                    <ChevronIcon open={showAll} />
                </button>
            )}
        </div>
    );
}
